import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageCircle } from 'lucide-react';
import Header from '../components/site/Header';
import Footer from '../components/site/Footer';
import Reveal from '../components/site/Reveal';
import { getCategories, getProducts } from '../api';
import { CONTACT_INFO } from '../data/content';

const CategoryPage = ({ category }) => {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    Promise.all([getCategories(), getProducts(category)])
      .then(([cats, prods]) => {
        setMeta(cats.find((c) => c.slug === category) || null);
        setProducts(prods);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category]);

  const whatsappFor = (name) =>
    `https://wa.me/${CONTACT_INFO.whatsappNumber}?text=${encodeURIComponent(
      `Olá! Tenho interesse no produto: ${name}`
    )}`;

  return (
    <div className="min-h-screen bg-[#040D1A] text-slate-100">
      <div className="grain-overlay" aria-hidden="true" />
      <Header />

      <section className="relative pt-36 pb-16 overflow-hidden">
        {meta && (
          <div className="absolute inset-0 opacity-15">
            <img src={meta.cover_image} alt="" aria-hidden="true" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#040D1A]/70 via-[#040D1A]/85 to-[#040D1A]" />
          </div>
        )}
        <div className="container mx-auto px-4 relative z-10">
          <button
            data-testid="category-back-button"
            onClick={() => navigate('/')}
            className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-amber-300 border border-amber-400/30 rounded-full px-5 py-2.5 mb-10 hover:bg-amber-400/10 hover:border-amber-300 transition-all"
          >
            <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" />
            Voltar
          </button>
          <h1
            data-testid="category-title"
            className="font-display font-extrabold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white"
          >
            {meta?.title || 'Produtos'}
          </h1>
          <p className="mt-4 max-w-2xl text-slate-300 text-base sm:text-lg">
            {meta?.description}
          </p>
        </div>
      </section>

      <section className="pb-28">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map((n) => (
                <div key={n} className="h-96 rounded-2xl border border-white/10 bg-white/[0.03] animate-pulse" />
              ))}
            </div>
          ) : (
            <div data-testid="category-products-grid" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {products.map((product, i) => (
                <Reveal key={product.id} delay={i * 0.06} className="h-full">
                  <div
                    data-testid={`product-card-${product.id}`}
                    className="group h-full rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm overflow-hidden hover:border-amber-400/40 hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(252,211,77,0.1)] transition-all duration-500"
                  >
                    <div className="relative h-64 flex items-center justify-center p-6 bg-[#0A2847]/30">
                      <div className="absolute inset-0 spotlight-yellow opacity-60 pointer-events-none" />
                      <img
                        src={product.image}
                        alt={product.name}
                        className="relative w-full h-full object-contain drop-shadow-[0_16px_28px_rgba(0,0,0,0.5)] transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <div className="p-6">
                      <h3 className="font-display font-bold uppercase text-lg text-white tracking-tight">
                        {product.name}
                      </h3>
                      <p className="mt-2 text-sm text-slate-400 leading-relaxed min-h-[60px]">
                        {product.description}
                      </p>
                      <a
                        data-testid={`product-whatsapp-${product.id}`}
                        href={whatsappFor(product.name)}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-5 inline-flex w-full items-center justify-center gap-2 bg-amber-400 text-[#0A2847] font-display font-bold uppercase tracking-wide text-xs px-6 py-3.5 rounded-full hover:bg-amber-300 hover:shadow-[0_0_24px_rgba(252,211,77,0.35)] transition-all duration-300"
                      >
                        <MessageCircle className="w-4 h-4" />
                        Solicitar Orçamento
                      </a>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CategoryPage;
